import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { adminApi, type AdminListingItem } from '@/services/api';

export default function AdminSupplyReviewPage() {
  const [items, setItems] = useState<AdminListingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<AdminListingItem | null>(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    adminApi
      .listSupplies({ status: filter, page: 1, pageSize: 100 })
      .then((res) => setItems(res.list))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAction = (id: number, action: 'approve' | 'reject') => {
    adminApi.auditSupply(id, action).then(fetchData).catch(() => {});
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground-950">供给审核</h2>
          <p className="text-sm text-foreground-500 mt-0.5">
            待审核 {items.filter((i) => i.auditStatus === 'pending').length} 条
          </p>
        </div>

        <div className="flex items-center gap-2">
          {['', 'pending', 'approved', 'rejected'].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                filter === s
                  ? 'bg-foreground-950 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {s === '' ? '全部' : s === 'pending' ? '待审核' : s === 'approved' ? '已通过' : '已驳回'}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200">
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">企业</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">年份</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap hidden sm:table-cell">项目类型</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">数量</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">报价</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">状态</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">操作</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-10 text-foreground-400">加载中...</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-10 text-foreground-400">暂无数据</td></tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id} className="border-b border-background-100 hover:bg-background-50/50">
                      <td className="px-4 py-3 font-medium text-foreground-900 whitespace-nowrap">{item.company}</td>
                      <td className="px-4 py-3 text-foreground-700">{item.year}</td>
                      <td className="px-4 py-3 text-foreground-600 hidden sm:table-cell">{item.projectType.join('、')}</td>
                      <td className="px-4 py-3 text-foreground-700">{item.quantity.toLocaleString()}</td>
                      <td className="px-4 py-3 text-foreground-700">{item.price}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          item.auditStatus === 'pending' ? 'bg-amber-100 text-amber-700' :
                          item.auditStatus === 'approved' ? 'bg-green-100 text-green-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {item.auditStatus === 'pending' ? '待审核' : item.auditStatus === 'approved' ? '已通过' : '已驳回'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {item.auditStatus === 'pending' ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleAction(item.id, 'approve')}
                              className="px-3 py-1 rounded-md text-xs font-medium text-white bg-green-600 hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              通过
                            </button>
                            <button
                              onClick={() => handleAction(item.id, 'reject')}
                              className="px-3 py-1 rounded-md text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              驳回
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setSelectedItem(item)}
                            className="px-2.5 py-1 rounded-md text-xs text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
                          >
                            查看
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {selectedItem && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/40" onClick={() => setSelectedItem(null)} />
            <div className="relative bg-white rounded-xl p-5 w-full max-w-sm border border-background-200 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold">挂牌详情</h3>
                <button onClick={() => setSelectedItem(null)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer">
                  <i className="ri-close-line" />
                </button>
              </div>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between"><span className="text-foreground-500">企业</span><span className="text-foreground-900 font-medium">{selectedItem.company}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">年份</span><span className="text-foreground-900">{selectedItem.year}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">项目类型</span><span className="text-foreground-900">{selectedItem.projectType.join('、')}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">数量</span><span className="text-foreground-900">{selectedItem.quantity.toLocaleString()} 张</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">报价</span><span className="text-foreground-900">{selectedItem.price}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">发布时间</span><span className="text-foreground-900">{new Date(selectedItem.publishDate).toLocaleString('zh-CN')}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">状态</span><span className={selectedItem.auditStatus === 'approved' ? 'text-green-600 font-medium' : selectedItem.auditStatus === 'rejected' ? 'text-red-600 font-medium' : 'text-amber-600 font-medium'}>{selectedItem.auditStatus === 'pending' ? '待审核' : selectedItem.auditStatus === 'approved' ? '已通过' : '已驳回'}</span></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
